import React, { FormEventHandler, useState } from 'react'
import styled from 'styled-components/macro'
import { toast } from 'react-toastify'
import log from 'loglevel'
import { RpcError } from 'grpc-web'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import { useDeleteFrameworkSection } from 'src/pages/Frameworks/frameworks.hooks'
import { getErrorMessage } from 'src/Utils/globalHelpers'
import { SectionInfo } from 'src/pages/Frameworks/frameworks.types'
import { ModalForm, ModalFormId } from '../..'

const WarningList = styled.ul`
  margin: 12px 0 0;
  padding-left: 20px;
`

type Props = {
  selectedSection: SectionInfo | null
  clearSelectedSection: () => void
}

export const DeleteFrameworkSectionConfirmModal = ({
  selectedSection,
  clearSelectedSection,
}: Props): JSX.Element => {
  const show = useIsShowModal(ModalFormId.DELETE_FRAMEWORK_SECTION_CONFIRM)
  const hideModal = useHideModal({
    modalId: ModalFormId.DELETE_FRAMEWORK_SECTION_CONFIRM,
  })
  const deleteFrameworkSection = useDeleteFrameworkSection()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const closeAndReset = () => {
    hideModal()
    setIsSubmitting(false)
    clearSelectedSection()
  }

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault()
    const modelId = selectedSection?.modelId
    if (!selectedSection || !modelId) return
    setIsSubmitting(true)
    try {
      await deleteFrameworkSection(modelId)
      toast.success('Section deleted.')
      closeAndReset()
    } catch (err) {
      const errorMessage = getErrorMessage(err as RpcError)
      toast.error(errorMessage.message)
      if (errorMessage.shouldLog) {
        log.error('DeleteFrameworkSectionConfirmModal submit failed', err)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ModalForm
      show={show}
      hide={closeAndReset}
      formId={ModalFormId.DELETE_FRAMEWORK_SECTION_CONFIRM}
      size="lg"
      title="Delete section?"
      submitText="Delete section"
      disableSubmitButton={isSubmitting || !selectedSection}
    >
      <form
        id={ModalFormId.DELETE_FRAMEWORK_SECTION_CONFIRM}
        onSubmit={onSubmit}
      >
        {selectedSection?.name ?? 'This section'} and its linked requirements
        will be permanently removed.
        <WarningList>
          <li>This action cannot be undone.</li>
          <li>All requirements in this section will be deleted.</li>
        </WarningList>
      </form>
    </ModalForm>
  )
}
